import _ from 'lodash'
import {FC} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonVariant} from '@/components/common/content/ButtonTypes'
import {Text, TextVariant} from '@/components/common/content/Text'
import {usePreferences} from '@/components/context/Preferences'
import {PricingServiceTableItem} from '@/components/pricing/services/PricingServiceTableItem'
import {getRegionalCountryList} from '@/content/countryCards'
import {useServiceCommonContent} from '@/content/service-request/common'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {getMinRegistrationPrice} from '@/utils/pricing'
import {routeTo} from '@/utils/router'
import {LegalServiceContent} from '@/utils/service-request/types'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'
import {getServicePrices} from '@/utils/supported-services/services'
import {supportedServices} from '@/utils/supported-services/supported-services'

type ServiceRequestPricingTableProps = {
  description?: string
  services: LegalServiceContent[]
  buttonText?: string
  onClick?: () => void
  note?: string
}

export const ServiceRequestPricingTable: FC<ServiceRequestPricingTableProps> = ({
  description,
  services,
  buttonText,
  onClick,
  note,
}) => {
  const {t} = useTranslation()
  const {currency, origin, destination} = usePreferences()
  const {title} = useServiceCommonContent().pricingTable
  const countryList = getRegionalCountryList(origin, destination)

  const minTmRegistrationPrice = getMinRegistrationPrice(currency, countryList)
  const serviceKey = supportedServices.Legal

  const servicesWithPrices = services.map(({key, title, description, linkUrl, priceNote}) => ({
    key,
    title: t(title),
    description: t(description),
    linkUrl,
    price:
      key === 'tm-registration'
        ? minTmRegistrationPrice
        : getServicePrices()[serviceKey][key]?.[currency],
    priceNote: priceNote ? t(priceNote) : undefined,
    priceLinkUrl: key === 'tm-registration' ? routeTo.pricing() : undefined,
  }))

  return (
    <>
      <Text variant={TextVariant.h2} align="center">
        {t(title)}
      </Text>
      {description && (
        <div className="subtitle">
          <Text variant={TextVariant.text20} align="center" color={theme.palette.text.light}>
            {description}
          </Text>
        </div>
      )}
      <ul className="table">
        {_.sortBy(servicesWithPrices, 'price').map(
          ({key, title, description, linkUrl, price, priceNote, priceLinkUrl}) => (
            <PricingServiceTableItem
              key={key}
              title={title}
              description={description}
              linkUrl={linkUrl}
              price={price}
              priceNote={priceNote}
              priceLinkUrl={priceLinkUrl}
              isDescriptionHiddenOnMobile
            />
          ),
        )}
      </ul>
      {buttonText && onClick && (
        <div className="button-wrapper">
          <Button type="button" onClick={onClick} variant={ButtonVariant.primary}>
            {buttonText}
          </Button>
        </div>
      )}
      {note && (
        <div className="note">
          <Text
            variant={TextVariant.caption}
            element="p"
            align="center"
            color={theme.palette.text.light}
          >
            {note}
          </Text>
        </div>
      )}

      <style jsx>{`
        .subtitle {
          max-width: 850px;
          margin: ${theme.spacing[3.5]} auto 0;
        }
        .table {
          padding: ${theme.spacing[3]} 0;
          position: relative;
          background-color: ${theme.palette.background.default};
          border: 1px solid ${theme.palette.gray[200]};
          border-radius: 16px;
          margin-top: ${theme.spacing[7]};
        }
        .button-wrapper {
          text-align: center;
          margin-top: ${theme.spacing[6]};
        }
        .note {
          max-width: 450px;
          margin: ${theme.spacing[3]} auto 0;
        }
        @media ${MOBILE_SCREENSIZE} {
          .subtitle {
            margin-top: ${theme.spacing[3]};
          }
          .table,
          .button-wrapper {
            margin-top: ${theme.spacing[5]};
          }
        }
      `}</style>
    </>
  )
}
